import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../schema.dexie";
import { getCriancaAtiva } from "../../db/queries";
import { dataNascimentoFmt, diasAtras, idade } from "../../lib/format";
import { baixarCSV } from "../../lib/export";
import { TendenciaLabel } from "../../ui/Tendencia";
import { calcularDerivadosAtividade } from "../domain";
import { fraseParticipacao } from "../evolucao";
import { momentosParaCSV } from "../export";
import { listarAtividades } from "../queries";
import {
  FATOR_LABEL,
  INTENSIDADE_LABEL,
  PARTICIPACAO_LABEL,
  SISTEMA_LABEL,
  type AtividadePsicomotora,
  type MomentoPsicomotor,
} from "../types";
import { AtividadeFoto } from "../ui/AtividadeFoto";
import { StatusAtividadeBadge } from "../ui/StatusAtividadeBadge";
import { ParticipacaoTimeline } from "../ui/ParticipacaoTimeline";
import { EfeitoBadge } from "../ui/EfeitoBadge";

/**
 * P0.7 — relatório legível por um psicomotricista / TO / fisio, sem jargão. Leitura
 * na TELA + "gerar PDF" (impressão estilizada, client-side). CSV é export secundário
 * de dados crus. Nada sai do aparelho sem esta ação explícita (P0.8).
 */
const PERIODOS: { v: number | "tudo"; r: string }[] = [
  { v: 30, r: "últimos 30 dias" },
  { v: 90, r: "últimos 90 dias" },
  { v: 180, r: "últimos 6 meses" },
  { v: "tudo", r: "todo o histórico" },
];

export function RelatorioCorpo() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const atividades = useLiveQuery(() => listarAtividades(true)) ?? [];
  const momentos = useLiveQuery(() => db.momento_psm.toArray()) ?? [];
  const [dias, setDias] = useState<number | "tudo">("tudo");
  const [feito, setFeito] = useState(false);

  const inicioMs = dias === "tudo" ? 0 : Date.now() - dias * 86400000;
  const noPeriodo = (m: MomentoPsicomotor) => new Date(m.data_hora).getTime() >= inicioMs;

  const momentosPeriodo = momentos.filter((m) => m.confirmado && noPeriodo(m));
  const ativas = atividades.filter((a) => !a.arquivado);
  const comRegistro = ativas
    .filter((a) => momentosPeriodo.some((m) => m.atividade_id === a.id))
    .sort((a, b) => a.nome.localeCompare(b.nome));

  const nFavoritas = ativas.filter((a) => a.status === "favorita").length;

  const avancaram = comRegistro.filter((a) => {
    const daAtiv = momentosPeriodo
      .filter((m) => m.atividade_id === a.id)
      .sort((x, y) => x.data_hora.localeCompare(y.data_hora));
    const primeiro = daAtiv[0]?.passo_participacao ?? 0;
    const max = Math.max(...daAtiv.map((m) => m.passo_participacao));
    return max > primeiro;
  }).length;

  const periodoLabel = PERIODOS.find((p) => p.v === dias)?.r ?? "período";
  const hojeFmt = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const nomePaciente = crianca?.nome_completo || crianca?.apelido || "paciente";
  const nascFmt = dataNascimentoFmt(crianca?.data_nascimento ?? null);
  const anos = idade(crianca?.data_nascimento ?? null);

  async function exportarCsv() {
    const csv = momentosParaCSV(momentosPeriodo, atividades);
    await baixarCSV(csv, `gingado-momentos-${new Date().toISOString().slice(0, 10)}.csv`);
    setFeito(true);
    setTimeout(() => setFeito(false), 2500);
  }

  return (
    <main className="tela relatorio">
      <div className="no-print">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <h1 className="tela-titulo">relatório</h1>
        <p className="tela-sub">
          a evolução da participação e o que regula o tônus, pra levar à terapia. nada
          sai do aparelho sem você gerar.
        </p>

        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <label>período</label>
            <select
              value={String(dias)}
              onChange={(e) =>
                setDias(e.target.value === "tudo" ? "tudo" : Number(e.target.value))
              }
            >
              {PERIODOS.map((p) => (
                <option key={String(p.v)} value={String(p.v)}>
                  {p.r}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="secao-titulo" style={{ marginTop: "var(--s5)" }}>
          prévia do relatório
        </p>
      </div>

      <article className="folha">
        <header className="rel-head">
          <div>
            <h2 className="rel-titulo">relatório de participação psicomotora</h2>
            <p className="rel-sub">
              {nomePaciente} · {periodoLabel}
            </p>
            {(nascFmt || anos !== null) && (
              <p className="rel-ident">
                nascimento: {nascFmt ?? "—"}
                {anos !== null ? ` · ${anos} ${anos === 1 ? "ano" : "anos"}` : ""}
              </p>
            )}
          </div>
          <div className="rel-data">gerado em {hojeFmt}</div>
        </header>

        <section className="rel-stats">
          <div className="rel-stat">
            <span className="n">{comRegistro.length}</span>
            <span className="r">atividades trabalhadas</span>
          </div>
          <div className="rel-stat">
            <span className="n">{momentosPeriodo.length}</span>
            <span className="r">momentos</span>
          </div>
          <div className="rel-stat">
            <span className="n">{avancaram}</span>
            <span className="r">avançaram na escada</span>
          </div>
          <div className="rel-stat">
            <span className="n">{nFavoritas}</span>
            <span className="r">favoritas no total</span>
          </div>
        </section>

        {comRegistro.length === 0 ? (
          <div className="vazio">
            sem momentos registrados neste período. registre alguns e o relatório se
            preenche.
          </div>
        ) : (
          <section className="rel-lista">
            {comRegistro.map((a) => (
              <RelatorioAtividade key={a.id} atividade={a} momentos={momentosPeriodo} />
            ))}
          </section>
        )}

        <footer className="rel-rodape">
          {(crianca?.responsavel_nome || crianca?.responsavel_cpf) && (
            <p className="rel-cpf">
              {crianca?.responsavel_nome
                ? `responsável (pais/guardião): ${crianca.responsavel_nome}`
                : ""}
              {crianca?.responsavel_nome && crianca?.responsavel_cpf ? " · " : ""}
              {crianca?.responsavel_cpf ? `CPF: ${crianca.responsavel_cpf}` : ""}
            </p>
          )}
          <p>
            escada de participação (0–9): presença · tolerância · orientação · exploração ·
            experimentação dirigida · realização com apoio · imitação · realização autônoma ·
            jogo a dois · iniciativa. subir um degrau já é progresso.
          </p>
          <p>
            gerado pelo Gingado — documenta a participação corporal e a regulação tônica;
            apoia o psicomotricista / TO, não o substitui.
          </p>
        </footer>
      </article>

      <div className="no-print" style={{ marginTop: "var(--s5)" }}>
        <p className="secao-titulo">exportar</p>
        <button
          className="btn btn-primario btn-bloco btn-grande"
          disabled={comRegistro.length === 0}
          onClick={() => window.print()}
        >
          🖨️ gerar PDF
        </button>
        <button
          className="btn btn-fantasma btn-bloco"
          style={{ marginTop: "var(--s2)" }}
          disabled={momentosPeriodo.length === 0}
          onClick={exportarCsv}
        >
          exportar dados crus (csv)
        </button>
        {feito && (
          <p style={{ textAlign: "center", color: "var(--accent-forte)" }}>csv exportado ✓</p>
        )}
        <p className="meta" style={{ color: "var(--text-muted)", marginTop: "var(--s2)" }}>
          o backup completo (.sqlite, inclui todos os módulos e fotos) fica no relatório do Pitada (alimentar).
        </p>
      </div>
    </main>
  );
}

function RelatorioAtividade({
  atividade,
  momentos,
}: {
  atividade: AtividadePsicomotora;
  momentos: MomentoPsicomotor[];
}) {
  const d = calcularDerivadosAtividade(atividade.id, momentos);
  const daAtiv = momentos.filter((m) => m.atividade_id === atividade.id);
  const psm = atividade.psicomotor;
  const chips = [
    psm.fator_alvo && FATOR_LABEL[psm.fator_alvo],
    ...psm.sistema.map((s) => SISTEMA_LABEL[s]),
    psm.intensidade && INTENSIDADE_LABEL[psm.intensidade],
  ].filter(Boolean) as string[];

  return (
    <div className="rel-card">
      <div className="rel-card-cab">
        <AtividadeFoto midias={atividade.midias} nome={atividade.nome} tipo={atividade.tipo} />
        <div className="crescer">
          <div className="row-entre">
            <span className="rel-nome">{atividade.nome}</span>
            <StatusAtividadeBadge status={atividade.status} />
          </div>
          <div className="rel-card-meta">
            {d.n_momentos} {d.n_momentos === 1 ? "momento" : "momentos"} ·{" "}
            {diasAtras(d.dias_desde_ultimo)} · <TendenciaLabel tendencia={d.tendencia} /> ·{" "}
            <EfeitoBadge efeito={d.efeito_regulatorio} />
          </div>
        </div>
      </div>

      <ParticipacaoTimeline momentos={daAtiv} />
      <p className="evol-frase">{fraseParticipacao(atividade.nome, daAtiv, d)}</p>

      <div className="meta" style={{ color: "var(--text-muted)" }}>
        maior passo: {d.passo_maximo === null ? "—" : PARTICIPACAO_LABEL[d.passo_maximo]}
      </div>

      {chips.length > 0 && (
        <div className="chips" style={{ marginTop: "var(--s2)" }}>
          {chips.map((c) => (
            <span className="chip" key={c}>
              {c}
            </span>
          ))}
        </div>
      )}

      {atividade.observacoes && <p className="rel-obs">“{atividade.observacoes}”</p>}
    </div>
  );
}
